import { cn } from '@/lib/utils';
import {
  LazyMotion,
  domAnimation,
  m,
  useReducedMotion,
  type Variants,
} from 'framer-motion';
import {
  HTMLAttributes,
  PropsWithChildren,
  createContext,
  useContext,
} from 'react';

/**
 * framer-motion redeclares these handlers with its own signatures, so they have
 * to be dropped before HTMLAttributes can be spread onto a motion element.
 */
type DivProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  | 'onAnimationStart'
  | 'onAnimationEnd'
  | 'onAnimationIteration'
  | 'onDrag'
  | 'onDragStart'
  | 'onDragEnd'
  | 'onTransitionEnd'
>;

/*
 * The site's only motion primitive.
 *
 * Two moments, deliberately: one orchestrated sequence on the home hero at page
 * load, and a quiet rise-in on section headings and card grids as they scroll
 * into view. Nothing else animates. Reduced-motion preference short-circuits to
 * plain markup, so content never depends on an animation completing.
 */

const EASE = [0.22, 1, 0.36, 1] as const;
const DURATION = 0.45;

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: DURATION, ease: EASE } },
};

/** Set inside a RevealGroup so RevealItem knows the parent drives the timing. */
const GroupContext = createContext(false);

type RevealProps = PropsWithChildren<DivProps> & {
  delay?: number;
};

/** A single element that rises in the first time it enters the viewport. */
const Reveal = ({ children, className, delay = 0, ...props }: RevealProps) => {
  const reduced = useReducedMotion();

  if (reduced) {
    return (
      <div className={className} {...props}>
        {children}
      </div>
    );
  }

  return (
    <LazyMotion features={domAnimation}>
      <m.div
        className={className}
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '0px 0px -10% 0px' }}
        transition={{ duration: DURATION, delay, ease: EASE }}
        {...props}
      >
        {children}
      </m.div>
    </LazyMotion>
  );
};

type RevealGroupProps = PropsWithChildren<DivProps> & {
  /**
   * `inView` (default) plays when the group scrolls into view; `load` plays
   * immediately on mount, which is what the home hero uses.
   */
  trigger?: 'inView' | 'load';
  stagger?: number;
  delay?: number;
};

/** Staggers its RevealItem children. */
const RevealGroup = ({
  children,
  className,
  trigger = 'inView',
  stagger = 0.06,
  delay = 0,
  ...props
}: RevealGroupProps) => {
  const reduced = useReducedMotion();

  if (reduced) {
    return (
      <div className={className} {...props}>
        {children}
      </div>
    );
  }

  const container: Variants = {
    hidden: {},
    show: { transition: { staggerChildren: stagger, delayChildren: delay } },
  };

  return (
    <GroupContext.Provider value>
      {/* Also covers RevealItem below: its m.div only renders once inGroup
          is true, i.e. only ever nested under this provider, so it reads
          this LazyMotion instead of needing its own. */}
      <LazyMotion features={domAnimation}>
        <m.div
          className={className}
          variants={container}
          initial="hidden"
          {...(trigger === 'load'
            ? { animate: 'show' }
            : {
                whileInView: 'show',
                viewport: { once: true, margin: '0px 0px -10% 0px' },
              })}
          {...props}
        >
          {children}
        </m.div>
      </LazyMotion>
    </GroupContext.Provider>
  );
};

/** One step of a RevealGroup's sequence. */
const RevealItem = ({
  children,
  className,
  ...props
}: PropsWithChildren<DivProps>) => {
  const reduced = useReducedMotion();
  const inGroup = useContext(GroupContext);

  if (reduced || !inGroup) {
    return (
      <div className={cn(className)} {...props}>
        {children}
      </div>
    );
  }

  return (
    <m.div className={className} variants={itemVariants} {...props}>
      {children}
    </m.div>
  );
};

export { Reveal, RevealGroup, RevealItem };
export default Reveal;
